import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, HostListener, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { HeroUserPillComponent } from './hero-user-pill.component';
import { PortalNavbarComponent } from './portal-navbar.component';
import { AuthService } from '../services/auth.service';
import { OrganizationService } from '../services/organization.service';
import { ManagedUserListItemDto, UserManagementService } from '../services/user-management.service';
import { TeamMemberArtifactDto, TeamMemberMediaService } from '../services/team-member-media.service';
import {
  UserNotificationPreferenceDto,
  UserNotificationPreferenceService
} from '../services/user-notification-preference.service';

type DeviceView = 'mobile' | 'tablet' | 'desktop';
type ArtifactType = 'Sound' | 'Video';
type ViewPreference = 'auto' | DeviceView;

interface MediaFilterOrganizationOption {
  id: number;
  name: string;
}

interface MediaFilterUserOption {
  id: number;
  label: string;
}

interface UploadValidationErrorDetails {
  artifactType?: string;
  maxBytes?: number;
  actualBytes?: number;
  allowedExtensions?: string[];
  allowedContentTypes?: string[];
  fileName?: string;
  helpUrl?: string;
}

interface UploadValidationErrorPayload {
  code?: string;
  message?: string;
  details?: UploadValidationErrorDetails;
}

@Component({
  selector: 'app-team-member-media-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, PortalNavbarComponent, HeroUserPillComponent],
  template: `
    <div class="page">
      <app-portal-navbar></app-portal-navbar>

      <section class="page-hero">
        <div class="page-hero-inner">
          <a routerLink="/" class="home-btn">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
            Home
          </a>
          <app-hero-user-pill></app-hero-user-pill>
          <div class="hero-head">
            <div class="hero-icon hero-icon--cyan">
              <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                <path d="M23 7l-7 5 7 5V7z"/>
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
              </svg>
            </div>
            @if (organizationName()) {
              <p class="hero-org-name">{{ organizationName() }}</p>
            }
          </div>
          <h1 class="page-title">My Media</h1>
          <div class="hero-actions">
            <button class="consent-toggle" [class.consent-toggle--on]="notificationPreference()?.smsNotificationsEnabled === true" (click)="openSmsConsentModal()">
              <span class="consent-toggle__dot" [class.consent-toggle__dot--on]="notificationPreference()?.smsNotificationsEnabled"></span>
              SMS Notifications: {{ notificationPreference()?.smsNotificationsEnabled ? 'Opted In' : 'Not Opted In' }}
            </button>
          </div>
          <p class="page-sub">Upload and manage your own custom audio/video files for game playback.</p>
          <p class="org-meta">{{ filterSummary() }}</p>
        </div>
      </section>

      <section class="page-body">
        <div class="page-body-inner">
          @if (forbidden()) {
            <div class="status-banner status-banner--error">Access denied. This page is for TeamMember media management.</div>
          } @else {
            @if (message()) {
              <div class="status-banner">{{ message() }}</div>
            }

            <div class="card">
              <div class="card-hd">
                <span class="card-title">View Mode</span>
                <span class="card-count">Detected: {{ detectedView() | titlecase }}</span>
              </div>
              <div class="view-switcher">
                <button class="btn btn--ghost" [class.btn--active]="viewPreference() === 'auto'" (click)="setViewPreference('auto')">Auto</button>
                <button class="btn btn--ghost" [class.btn--active]="viewPreference() === 'mobile'" (click)="setViewPreference('mobile')">Mobile</button>
                <button class="btn btn--ghost" [class.btn--active]="viewPreference() === 'tablet'" (click)="setViewPreference('tablet')">Tablet</button>
                <button class="btn btn--ghost" [class.btn--active]="viewPreference() === 'desktop'" (click)="setViewPreference('desktop')">Desktop</button>
              </div>
            </div>

            <div class="card">
              <div class="card-hd">
                <span class="card-title">Upload Media</span>
                <button class="consent-toggle" [class.consent-toggle--on]="notificationPreference()?.mediaUploadConsentEnabled === true" (click)="openMediaConsentModal()" [attr.aria-pressed]="notificationPreference()?.mediaUploadConsentEnabled === true">
                  <span class="consent-toggle__dot" [class.consent-toggle__dot--on]="notificationPreference()?.mediaUploadConsentEnabled"></span>
                  Media Upload Consent
                </button>
              </div>
              @if (mediaConsentGranted()) {
                <div class="form-grid">
                  <div class="field">
                    <label class="field-label" for="artifact-type">Media Type</label>
                    <select id="artifact-type" class="field-input" [(ngModel)]="draftArtifactType">
                      <option value="Sound">Sound</option>
                      <option value="Video">Video</option>
                    </select>
                  </div>

                  <div class="field">
                    <label class="field-label" for="artifact-category">Category</label>
                    <select id="artifact-category" class="field-input" [(ngModel)]="draftArtifactCategory">
                      <option value="" disabled>Select category</option>
                      @for (category of categoryOptions; track category.value) {
                        <option [value]="category.value">{{ category.label }}</option>
                      }
                    </select>
                  </div>

                  <div class="field">
                    <label class="field-label" for="artifact-file">File</label>
                    <input id="artifact-file" class="field-input" type="file" [accept]="acceptedMimeTypes" (change)="onUploadFileSelected($event)" />
                  </div>
                </div>

                <div class="action-bar">
                  <button class="btn btn--primary" [disabled]="busy() || !organizationId() || !selectedUploadFile || !draftArtifactCategory" (click)="uploadMedia()">Upload</button>
                </div>
              } @else {
                <div class="consent-lock">Media uploads are disabled until you opt in to media upload consent.</div>
              }
            </div>

            <div class="card">
              <div class="card-hd">
                <span class="card-title">My Uploaded Media</span>
                <span class="card-count">{{ filteredArtifacts().length }} item{{ filteredArtifacts().length !== 1 ? 's' : '' }}</span>
              </div>
              @if (!mediaConsentGranted()) {
                <div class="consent-lock">Your media list is hidden until you opt in to media upload consent.</div>
              } @else {
                @if (conversionInProgressCount() > 0) {
                  <div class="conversion-banner">{{ conversionInProgressCount() }} video conversion{{ conversionInProgressCount() !== 1 ? 's are' : ' is' }} in progress. This list auto-refreshes.</div>
                }

                <div class="table-tools">
                  <div class="tool-group">
                    <label class="field-label" for="media-type-filter">Media type</label>
                    <select
                      id="media-type-filter"
                      class="tool-select"
                      [ngModel]="filterArtifactType()"
                      (ngModelChange)="filterArtifactType.set($event)">
                      <option value="All">All media types</option>
                      <option value="Sound">Sound only</option>
                      <option value="Video">Video only</option>
                    </select>
                  </div>

                  @if (isSiteAdmin()) {
                    <div class="tool-group">
                      <label class="field-label" for="organization-filter">Organization</label>
                      <select
                        id="organization-filter"
                        class="tool-select"
                        [ngModel]="selectedOrganizationFilterId"
                        (ngModelChange)="onOrganizationFilterChanged($event)">
                        <option [ngValue]="null">All organizations</option>
                        @for (organization of organizationFilterOptions; track organization.id) {
                          <option [ngValue]="organization.id">{{ organization.name }}</option>
                        }
                      </select>
                    </div>
                  }

                  @if (canFilterByUser()) {
                    <div class="tool-group">
                      <label class="field-label" for="user-filter">User</label>
                      <select
                        id="user-filter"
                        class="tool-select"
                        [ngModel]="selectedUserFilterId"
                        (ngModelChange)="onUserFilterChanged($event)">
                        <option [ngValue]="null">All users</option>
                        @for (user of userFilterOptions; track user.id) {
                          <option [ngValue]="user.id">{{ user.label }}</option>
                        }
                      </select>
                    </div>
                  }
                </div>

                @if (activeView() === 'desktop') {
                  <div class="table-wrap">
                    <div class="table-head">
                      <span>Type</span>
                      <span>File</span>
                      <span>Category</span>
                      <span>Status</span>
                      <span>Created</span>
                      <span>Actions</span>
                    </div>
                    @for (artifact of filteredArtifacts(); track artifact.id) {
                      <div class="table-row">
                        <span>{{ artifact.artifactType }}</span>
                        <span>{{ artifact.fileName }}</span>
                        <span>{{ artifact.artifactCategory || '—' }}</span>
                        <span><span [class]="statusClass(artifact)">{{ statusLabel(artifact) }}</span></span>
                        <span>{{ formatDate(artifact.dateCreated) }}</span>
                        <div class="row-actions">
                          <button class="btn btn--ghost" [disabled]="isConversionPending(artifact)" (click)="togglePlayback(artifact)">{{ activePreviewId() === artifact.id ? 'Hide' : 'Preview' }}</button>
                          <label class="btn btn--ghost file-btn">
                            Replace
                            <input type="file" [accept]="artifact.artifactType === 'Sound' ? '.mp3,.wav,.ogg,.wmv' : '.mp4,.wmv,.mov,.webm,.webp'" (change)="onReplaceSelected(artifact, $event)" />
                          </label>
                          <button class="btn btn--danger" (click)="deleteMedia(artifact.id)">Delete</button>
                        </div>
                      </div>
                      @if (activePreviewId() === artifact.id && (previewUrl(artifact.id) || previewErrorMessage())) {
                        <div class="preview-row">
                          @if (previewUrl(artifact.id)) {
                            @if (artifact.artifactType === 'Sound') {
                              <audio controls [src]="previewUrl(artifact.id)" (loadeddata)="clearMediaDecodeError()" (error)="onMediaDecodeError($event, artifact)"></audio>
                            } @else {
                              <video controls class="video-preview" [src]="previewUrl(artifact.id)" (loadeddata)="clearMediaDecodeError()" (error)="onMediaDecodeError($event, artifact)"></video>
                            }
                          }
                          @if (previewErrorMessage()) {
                            <div class="preview-error">{{ previewErrorMessage() }}</div>
                          }
                        </div>
                      }
                    } @empty {
                      <div class="empty">No media uploaded yet.</div>
                    }
                  </div>
                } @else {
                  <div class="mobile-list">
                    @for (artifact of filteredArtifacts(); track artifact.id) {
                      <div class="mobile-card">
                        <div class="mobile-card-title">{{ artifact.fileName }}</div>
                        <div class="mobile-card-meta">{{ artifact.artifactType }} · {{ artifact.artifactCategory || 'No category' }}</div>
                        <div class="mobile-card-meta"><span [class]="statusClass(artifact)">{{ statusLabel(artifact) }}</span></div>
                        <div class="mobile-card-meta">Created {{ formatDate(artifact.dateCreated) }}</div>
                        <div class="mobile-actions">
                          <button class="btn btn--ghost" [disabled]="isConversionPending(artifact)" (click)="togglePlayback(artifact)">{{ activePreviewId() === artifact.id ? 'Hide' : 'Preview' }}</button>
                          <label class="btn btn--ghost file-btn">
                            Replace
                            <input type="file" [accept]="artifact.artifactType === 'Sound' ? '.mp3,.wav,.ogg,.wmv' : '.mp4,.wmv,.mov,.webm,.webp'" (change)="onReplaceSelected(artifact, $event)" />
                          </label>
                          <button class="btn btn--danger" (click)="deleteMedia(artifact.id)">Delete</button>
                        </div>

                        @if (activePreviewId() === artifact.id && (previewUrl(artifact.id) || previewErrorMessage())) {
                          <div class="preview-row preview-row--stacked">
                            @if (previewUrl(artifact.id)) {
                              @if (artifact.artifactType === 'Sound') {
                                <audio controls [src]="previewUrl(artifact.id)" (loadeddata)="clearMediaDecodeError()" (error)="onMediaDecodeError($event, artifact)"></audio>
                              } @else {
                                <video controls class="video-preview" [src]="previewUrl(artifact.id)" (loadeddata)="clearMediaDecodeError()" (error)="onMediaDecodeError($event, artifact)"></video>
                              }
                            }
                            @if (previewErrorMessage()) {
                              <div class="preview-error">{{ previewErrorMessage() }}</div>
                            }
                          </div>
                        }
                      </div>
                    } @empty {
                      <div class="empty">No media uploaded yet.</div>
                    }
                  </div>
                }
              }
            </div>

            @if (showSmsConsentModal()) {
              <div class="modal-backdrop" (click)="closeConsentModals()">
                <div class="modal" (click)="$event.stopPropagation()">
                  <h3>SMS Notification Consent</h3>
                  <textarea class="consent-memo" readonly [value]="smsConsentDisclosureText"></textarea>
                  <label class="field-label" for="sms-phone">Phone Number</label>
                  <input id="sms-phone" class="field-input" type="tel" [(ngModel)]="notificationPhoneNumber" placeholder="+1XXXXXXXXXX" />
                  <div class="modal-actions">
                    <button class="btn btn--primary" (click)="applySmsConsent(true)">I Consent</button>
                    <button class="btn btn--danger" (click)="applySmsConsent(false)">I Do Not Consent</button>
                    <button class="btn btn--ghost" (click)="closeConsentModals()">Close</button>
                  </div>
                </div>
              </div>
            }

            @if (showMediaConsentModal()) {
              <div class="modal-backdrop" (click)="closeConsentModals()">
                <div class="modal" (click)="$event.stopPropagation()">
                  <h3>Media Upload Consent</h3>
                  <textarea class="consent-memo" readonly [value]="mediaUploadConsentDisclosureText"></textarea>
                  <div class="modal-actions">
                    <button class="btn btn--primary" (click)="applyMediaConsent(true)">I Consent</button>
                    <button class="btn btn--danger" (click)="applyMediaConsent(false)">I Do Not Consent</button>
                    <button class="btn btn--ghost" (click)="closeConsentModals()">Close</button>
                  </div>
                </div>
              </div>
            }

            @if (showUploadErrorModal()) {
              <div class="modal-backdrop" (click)="closeUploadErrorModal()">
                <div class="modal" role="dialog" aria-modal="true" aria-labelledby="upload-error-title" (click)="$event.stopPropagation()">
                  <h3 id="upload-error-title">{{ uploadErrorTitle() }}</h3>
                  <p class="status-banner status-banner--error">{{ uploadErrorSummary() }}</p>
                  @if (uploadErrorCode()) {
                    <p class="form-help">Code: {{ uploadErrorCode() }}</p>
                  }
                  @if (uploadErrorTips().length > 0) {
                    <ul class="upload-error-list">
                      @for (tip of uploadErrorTips(); track tip) {
                        <li>{{ tip }}</li>
                      }
                    </ul>
                  }
                  <div class="modal-actions">
                    <button class="btn btn--primary" (click)="closeUploadErrorModal()">Close</button>
                  </div>
                </div>
              </div>
            }
          }
        </div>
      </section>
    </div>
  `,
  styles: [`
    .page { min-height: 100vh; }

    .page-hero {
      background:
        radial-gradient(ellipse at 12% 60%, color-mix(in srgb, #06b6d4 8%, transparent) 0%, transparent 55%),
        linear-gradient(155deg, var(--color-bg-start) 0%, var(--color-bg-end) 100%);
      border-bottom: 1px solid var(--color-border);
      padding: 3rem 1.5rem 3.5rem;
    }

    .page-hero-inner,
    .page-body-inner {
      max-width: 1100px;
      margin: 0 auto;
    }

    .home-btn {
      display: inline-flex;
      align-items: center;
      gap: .35rem;
      color: var(--color-muted);
      text-decoration: none;
      font-size: .9rem;
      margin-bottom: .95rem;
    }

    .hero-icon {
      width: 3.25rem;
      height: 3.25rem;
      border-radius: .95rem;
      display: grid;
      place-items: center;
      margin-bottom: .9rem;
      border: 1px solid color-mix(in srgb, #06b6d4 45%, #0000);
      color: #67e8f9;
      background: color-mix(in srgb, #06b6d4 18%, transparent);
      box-shadow: inset 0 0 0 1px color-mix(in srgb, #06b6d4 24%, transparent);
    }

    .hero-head {
      display: flex;
      align-items: center;
      gap: .9rem;
      margin-bottom: .4rem;
    }

    .hero-org-name {
      margin: 0;
      font-size: clamp(1.15rem, 2.5vw, 1.75rem);
      font-weight: 700;
      letter-spacing: -.01em;
      color: var(--color-text);
    }

    .page-title {
      margin: 0;
      font-size: clamp(1.6rem, 3vw, 2.2rem);
      letter-spacing: -.01em;
    }

    .page-sub {
      margin: .5rem 0 .25rem;
      color: var(--color-muted);
      max-width: 60ch;
    }

    .hero-actions {
      margin-top: .5rem;
      margin-bottom: .35rem;
      display: flex;
      gap: .5rem;
      flex-wrap: wrap;
    }

    .org-meta {
      margin: 0;
      color: var(--color-muted);
      font-size: .85rem;
    }

    .page-body { padding: 1.5rem; }

    .card {
      border: 1px solid var(--color-border);
      border-radius: .95rem;
      overflow: hidden;
      margin-bottom: 1rem;
      background: color-mix(in srgb, var(--color-panel) 80%, transparent);
    }

    .card-hd {
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 1px solid var(--color-border);
      padding: .85rem 1rem;
      gap: .6rem;
      flex-wrap: wrap;
    }

    .card-title { font-weight: 600; }
    .card-count { color: var(--color-muted); font-size: .9rem; }

    .consent-toggle {
      display: inline-flex;
      align-items: center;
      gap: .5rem;
      border: 1px solid var(--color-border);
      border-radius: 999px;
      padding: .35rem .65rem;
      background: color-mix(in srgb, var(--color-surface-2) 70%, transparent);
      color: var(--color-text);
      cursor: pointer;
      font-size: .82rem;
      font-weight: 600;
    }

    .consent-toggle--on {
      border-color: color-mix(in srgb, var(--color-accent) 65%, transparent);
      box-shadow: inset 0 0 0 1px color-mix(in srgb, #22c55e 38%, transparent);
    }

    .consent-toggle__dot {
      width: .7rem;
      height: .7rem;
      border-radius: 999px;
      border: 1px solid color-mix(in srgb, #ef4444 40%, transparent);
      background: color-mix(in srgb, #ef4444 25%, transparent);
    }

    .consent-toggle__dot--on {
      border-color: color-mix(in srgb, #22c55e 40%, transparent);
      background: color-mix(in srgb, #22c55e 40%, transparent);
    }

    .status-banner {
      margin-bottom: .9rem;
      border: 1px solid color-mix(in srgb, var(--color-accent-soft) 45%, transparent);
      background: color-mix(in srgb, var(--color-accent-soft) 10%, transparent);
      color: var(--color-text);
      border-radius: .75rem;
      padding: .7rem .9rem;
      font-size: .92rem;
    }

    .status-banner--error {
      border-color: color-mix(in srgb, #ef4444 45%, transparent);
      background: color-mix(in srgb, #ef4444 12%, transparent);
    }

    .form-grid {
      padding: 1rem;
      display: grid;
      gap: .8rem;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    }

    .field { display: flex; flex-direction: column; gap: .35rem; }
    .field-label { font-size: .84rem; color: var(--color-muted); }

    .field-input,
    .tool-select {
      border: 1px solid var(--color-border);
      border-radius: .55rem;
      background: color-mix(in srgb, var(--color-surface-2) 80%, transparent);
      color: var(--color-text);
      font-size: .9rem;
      padding: .48rem .62rem;
    }

    .action-bar {
      padding: 0 1rem 1rem;
      display: flex;
      justify-content: flex-end;
    }

    .view-switcher {
      display: flex;
      gap: .5rem;
      padding: .8rem 1rem 1rem;
      flex-wrap: wrap;
    }

    .btn {
      border: 1px solid var(--color-border);
      border-radius: .55rem;
      background: transparent;
      color: var(--color-text);
      padding: .42rem .78rem;
      font-size: .86rem;
      font-weight: 600;
      cursor: pointer;
    }

    .btn:disabled { opacity: .5; cursor: not-allowed; }

    .btn--primary {
      background: color-mix(in srgb, var(--color-accent) 85%, #fff 15%);
      border-color: color-mix(in srgb, var(--color-accent) 80%, transparent);
      color: #fff;
    }

    .btn--ghost {
      background: color-mix(in srgb, var(--color-surface-2) 68%, transparent);
    }

    .btn--danger {
      border-color: color-mix(in srgb, #ef4444 45%, transparent);
      color: #fecaca;
      background: color-mix(in srgb, #7f1d1d 25%, transparent);
    }

    .btn--active {
      border-color: color-mix(in srgb, var(--color-accent) 65%, transparent);
      color: var(--color-accent-soft);
    }

    .table-tools {
      padding: 0.8rem 1rem;
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, max-content));
      gap: .75rem;
      align-items: end;
    }

    .tool-group {
      display: flex;
      flex-direction: column;
      gap: .35rem;
      min-width: 180px;
    }

    .conversion-banner {
      margin: .8rem 1rem 0;
      border: 1px solid color-mix(in srgb, #f59e0b 45%, transparent);
      background: color-mix(in srgb, #f59e0b 12%, transparent);
      color: #fde68a;
      border-radius: .6rem;
      padding: .55rem .75rem;
      font-size: .85rem;
    }

    .table-wrap { width: 100%; }

    .table-head,
    .table-row {
      display: grid;
      grid-template-columns: 110px 1.55fr 1fr 140px 150px 1.7fr;
      gap: .65rem;
      align-items: center;
      padding: .75rem 1rem;
    }

    .table-head {
      border-top: 1px solid var(--color-border);
      border-bottom: 1px solid var(--color-border);
      color: var(--color-muted);
      font-size: .82rem;
      font-weight: 600;
    }

    .table-row { border-bottom: 1px solid var(--color-border); }

    .row-actions {
      display: flex;
      align-items: center;
      gap: .45rem;
      flex-wrap: wrap;
    }

    .file-btn {
      position: relative;
      overflow: hidden;
    }

    .file-btn input[type='file'] {
      position: absolute;
      inset: 0;
      opacity: 0;
      cursor: pointer;
    }

    .preview-row {
      border-bottom: 1px solid var(--color-border);
      padding: .8rem 1rem 1rem;
    }

    .preview-row--stacked { padding-top: .6rem; }

    .preview-error {
      margin-top: .55rem;
      border: 1px solid color-mix(in srgb, #ef4444 45%, transparent);
      background: color-mix(in srgb, #ef4444 10%, transparent);
      color: #fecaca;
      border-radius: .5rem;
      padding: .45rem .6rem;
      font-size: .82rem;
    }

    audio,
    .video-preview {
      width: min(100%, 500px);
      border-radius: .65rem;
      border: 1px solid var(--color-border);
      background: #000;
    }

    .mobile-list {
      display: grid;
      gap: .75rem;
      padding: .8rem 1rem 1rem;
    }

    .mobile-card {
      border: 1px solid var(--color-border);
      border-radius: .75rem;
      padding: .75rem;
      background: color-mix(in srgb, var(--color-surface-2) 65%, transparent);
    }

    .mobile-card-title { font-weight: 600; margin-bottom: .2rem; }
    .mobile-card-meta { color: var(--color-muted); font-size: .84rem; }

    .status-pill {
      display: inline-flex;
      align-items: center;
      border: 1px solid var(--color-border);
      border-radius: 999px;
      padding: .15rem .45rem;
      font-size: .76rem;
      font-weight: 600;
      line-height: 1.1;
      white-space: nowrap;
    }

    .status-pill--pending {
      border-color: color-mix(in srgb, #f59e0b 40%, transparent);
      color: #fcd34d;
      background: color-mix(in srgb, #f59e0b 16%, transparent);
    }

    .status-pill--ready {
      border-color: color-mix(in srgb, #22c55e 40%, transparent);
      color: #86efac;
      background: color-mix(in srgb, #22c55e 14%, transparent);
    }

    .status-pill--error {
      border-color: color-mix(in srgb, #ef4444 45%, transparent);
      color: #fecaca;
      background: color-mix(in srgb, #ef4444 12%, transparent);
    }

    .mobile-actions {
      display: flex;
      gap: .45rem;
      margin-top: .65rem;
      flex-wrap: wrap;
    }

    .empty {
      color: var(--color-muted);
      padding: 1rem;
      text-align: center;
    }

    .consent-lock {
      margin: .9rem 1rem 1rem;
      border: 1px solid color-mix(in srgb, #f59e0b 45%, transparent);
      background: color-mix(in srgb, #f59e0b 12%, transparent);
      color: #fde68a;
      border-radius: .6rem;
      padding: .6rem .75rem;
      font-size: .85rem;
    }

    .modal-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.55);
      display: grid;
      place-items: center;
      z-index: 50;
      padding: 1rem;
    }

    .modal {
      width: min(560px, 100%);
      border: 1px solid var(--color-border);
      border-radius: .85rem;
      background: var(--color-bg-end);
      box-shadow: 0 24px 64px rgba(0, 0, 0, 0.45);
      padding: 1rem;
      display: grid;
      gap: .7rem;
    }

    .modal h3 {
      margin: 0;
      font-size: 1rem;
    }

    .modal-text {
      margin: 0;
      color: var(--color-muted);
      font-size: .88rem;
      line-height: 1.45;
    }

    .consent-memo {
      width: 100%;
      min-height: 9rem;
      resize: vertical;
      border: 1px solid var(--color-border);
      border-radius: .55rem;
      background: color-mix(in srgb, var(--color-surface-2) 80%, transparent);
      color: var(--color-text);
      padding: .65rem .75rem;
      font-size: .84rem;
      line-height: 1.4;
    }

    .modal-actions {
      display: flex;
      gap: .45rem;
      flex-wrap: wrap;
      justify-content: flex-end;
    }

    .upload-error-list {
      margin: 0;
      padding-left: 1rem;
      color: var(--color-muted);
      display: grid;
      gap: .3rem;
      font-size: .9rem;
    }

    @media (max-width: 980px) {
      .table-head,
      .table-row {
        grid-template-columns: 90px 1.3fr .95fr 130px 120px 1.45fr;
      }
    }
  `]
})
export class TeamMemberMediaPageComponent implements OnInit, OnDestroy {
  private static readonly PendingStatuses = new Set(['pendingconversion', 'processingconversion']);

  private readonly authService = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly organizationService = inject(OrganizationService);
  private readonly mediaService = inject(TeamMemberMediaService);
  private readonly userManagementService = inject(UserManagementService);
  private readonly notificationPreferenceService = inject(UserNotificationPreferenceService);

  readonly forbidden = signal(false);
  readonly busy = signal(false);
  readonly message = signal('');

  readonly organizationId = signal<number | null>(null);
  readonly organizationName = signal('');

  readonly artifacts = signal<TeamMemberArtifactDto[]>([]);
  readonly notificationPreference = signal<UserNotificationPreferenceDto | null>(null);
  readonly showSmsConsentModal = signal(false);
  readonly showMediaConsentModal = signal(false);
  readonly showUploadErrorModal = signal(false);
  readonly isTeamMemberOnly = signal(false);
  readonly isSiteAdmin = signal(false);
  readonly isOrgScopedAdmin = signal(false);
  readonly uploadErrorTitle = signal('Upload failed');
  readonly uploadErrorSummary = signal('Please verify media type and file size limits.');
  readonly uploadErrorCode = signal('');
  readonly uploadErrorTips = signal<string[]>([]);

  readonly detectedView = signal<DeviceView>(this.detectView(window.innerWidth));
  readonly viewPreference = signal<ViewPreference>('auto');
  readonly activeView = computed<DeviceView>(() => this.viewPreference() === 'auto' ? this.detectedView() : this.viewPreference() as DeviceView);
  readonly mediaConsentGranted = computed(() => !this.isTeamMemberOnly() || this.notificationPreference()?.mediaUploadConsentEnabled === true);

  readonly activePreviewId = signal<number | null>(null);
  readonly previewErrorMessage = signal('');

  organizationFilterOptions: MediaFilterOrganizationOption[] = [];
  userFilterOptions: MediaFilterUserOption[] = [];
  selectedOrganizationFilterId: number | null = null;
  selectedUserFilterId: number | null = null;
  private managedUsers: ManagedUserListItemDto[] = [];
  private pendingSelectedUserFilterId: number | null = null;

  readonly filterArtifactType = signal<'All' | 'Sound' | 'Video'>('All');
  draftArtifactType: ArtifactType = 'Sound';
  draftArtifactCategory = '';
  selectedUploadFile: File | null = null;
  notificationPhoneNumber = '';

  readonly categoryOptions = [
    { value: 'right-answer', label: 'Right Answer' },
    { value: 'wrong-answer', label: 'Wrong Answer' }
  ];

  readonly smsConsentDisclosureText = `By providing your phone number and clicking [Submit/Sign Up], you consent to receive automated text messages (e.g. event updates, media updates) from United Team Building at the number provided. Consent is not a condition of any purchase. Message and data rates may apply. Message frequency varies. Reply STOP to cancel or HELP for help. View our Terms of Service and Privacy Policy.`;

  readonly mediaUploadConsentDisclosureText = `User Content Submission Disclaimer
By uploading media to United Team Building, you acknowledge and agree to the following:
Ownership: You retain ownership of your content. However, you grant United Team Building a perpetual, worldwide, royalty-free license to use, reproduce, and display this media.
Legality: You warrant that you own the rights to this media and that it does not violate any third-party copyrights or trademarks.
Prohibited Content: You will not upload material that is illegal, defamatory, or pornographic.
No Liability: United Team Building is not responsible for any user-uploaded content and expressly disclaims all liability for damages arising from your use of the site.
Moderation: We reserve the right to remove any content at any time for any reason.`;

  private readonly previewUrlMap = new Map<number, string>();
  private conversionPollingHandle: ReturnType<typeof setInterval> | null = null;

  readonly filteredArtifacts = computed(() => {
    const sorted = [...this.artifacts()].sort((a, b) => Date.parse(b.dateCreated) - Date.parse(a.dateCreated));
    if (this.filterArtifactType() === 'All') {
      return sorted;
    }

    return sorted.filter(a => a.artifactType === this.filterArtifactType());
  });

  readonly conversionInProgressCount = computed(() =>
    this.artifacts().filter(a => a.artifactType === 'Video' && this.isConversionPending(a)).length);

  readonly canFilterByUser = computed(() => this.isSiteAdmin() || this.isOrgScopedAdmin());

  get acceptedMimeTypes(): string {
    return this.draftArtifactType === 'Sound'
      ? '.mp3,.wav,.ogg,.wmv,audio/mpeg,audio/wav,audio/ogg,video/x-ms-wmv,application/x-ms-wmv'
      : '.mp4,.wmv,.mov,.webm,.webp,video/mp4,video/x-ms-wmv,video/quicktime,video/webm,image/webp';
  }

  async ngOnInit(): Promise<void> {
    const isTeamMember = await this.authService.hasRoleAsync('TeamMember');
    const isSiteAdmin = await this.authService.hasRoleAsync('SiteAdmin');
    const isOrgAdmin = await this.authService.hasRoleAsync('OrgAdmin');
    const isEventPlanner = await this.authService.hasRoleAsync('EventPlanner');
    const isOrgScopedAdmin = isOrgAdmin || isEventPlanner;

    this.isSiteAdmin.set(isSiteAdmin);
    this.isOrgScopedAdmin.set(isOrgScopedAdmin);
    this.isTeamMemberOnly.set(isTeamMember && !isSiteAdmin && !isOrgAdmin && !isEventPlanner);

    if (!isTeamMember && !isSiteAdmin && !isOrgScopedAdmin) {
      this.forbidden.set(true);
      return;
    }

    await this.initializeContext();
  }

  ngOnDestroy(): void {
    for (const url of this.previewUrlMap.values()) {
      URL.revokeObjectURL(url);
    }
    this.previewUrlMap.clear();
    this.stopConversionPolling();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: UIEvent): void {
    const width = (event.target as Window).innerWidth;
    this.detectedView.set(this.detectView(width));
  }

  setViewPreference(preference: ViewPreference): void {
    this.viewPreference.set(preference);
  }

  async onOrganizationFilterChanged(value: number | null): Promise<void> {
    this.selectedOrganizationFilterId = value;
    this.selectedUserFilterId = null;
    this.populateUserFilterOptions();
    await this.loadArtifacts();
  }

  async onUserFilterChanged(value: number | null): Promise<void> {
    this.selectedUserFilterId = value;
    await this.loadArtifacts();
  }

  filterSummary(): string {
    if (this.isTeamMemberOnly()) {
      return 'Showing only your uploads';
    }

    if (this.isSiteAdmin()) {
      const orgLabel = this.selectedOrganizationFilterId
        ? this.organizationFilterOptions.find(o => o.id === this.selectedOrganizationFilterId)?.name ?? 'selected organization'
        : 'all organizations';
      const userLabel = this.selectedUserFilterId
        ? this.userFilterOptions.find(u => u.id === this.selectedUserFilterId)?.label ?? 'selected user'
        : 'all users';
      return `Showing ${userLabel} in ${orgLabel}`;
    }

    const userLabel = this.selectedUserFilterId
      ? this.userFilterOptions.find(u => u.id === this.selectedUserFilterId)?.label ?? 'selected user'
      : 'all users';
    return `Showing ${userLabel} in your organization`;
  }

  onUploadFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedUploadFile = input.files?.[0] ?? null;
  }

  async uploadMedia(): Promise<void> {
    if (!this.mediaConsentGranted()) {
      this.message.set('Media upload consent is required before uploading media.');
      return;
    }

    const organizationId = this.organizationId();
    if (!organizationId || !this.selectedUploadFile || !this.draftArtifactCategory) {
      return;
    }

    this.busy.set(true);
    this.message.set('');

    try {
      const created = await firstValueFrom(
        this.mediaService.uploadMedia(organizationId, this.draftArtifactType, this.draftArtifactCategory, this.selectedUploadFile)
      );

      this.selectedUploadFile = null;
      this.draftArtifactCategory = '';
      this.message.set(this.isConversionPending(created)
        ? 'Upload completed. Video conversion is in progress and will update automatically.'
        : 'Upload completed.');
      await this.loadArtifacts();
    } catch (error) {
      this.openUploadErrorModal('Upload failed.', error);
    } finally {
      this.busy.set(false);
    }
  }

  async onReplaceSelected(artifact: TeamMemberArtifactDto, event: Event): Promise<void> {
    if (!this.mediaConsentGranted()) {
      this.message.set('Media upload consent is required before replacing media.');
      return;
    }

    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }

    this.busy.set(true);
    this.message.set('');

    try {
      const updated = await firstValueFrom(this.mediaService.replaceMedia(artifact.id, file));
      this.clearPreviewFor(artifact.id);
      this.message.set(this.isConversionPending(updated)
        ? 'Media replaced. Video conversion is in progress and will update automatically.'
        : 'Media replaced.');
      const organizationId = this.organizationId();
      if (organizationId) {
        await this.loadArtifacts();
      }
    } catch (error) {
      this.openUploadErrorModal('Replace failed.', error);
    } finally {
      input.value = '';
      this.busy.set(false);
    }
  }

  async deleteMedia(id: number): Promise<void> {
    if (!this.mediaConsentGranted()) {
      this.message.set('Media upload consent is required before deleting media.');
      return;
    }

    this.busy.set(true);
    this.message.set('');

    try {
      await firstValueFrom(this.mediaService.deleteMedia(id));
      this.clearPreviewFor(id);
      this.message.set('Media deleted.');
      const organizationId = this.organizationId();
      if (organizationId) {
        await this.loadArtifacts();
      }
    } catch {
      this.message.set('Delete failed.');
    } finally {
      this.busy.set(false);
    }
  }

  async togglePlayback(artifact: TeamMemberArtifactDto): Promise<void> {
    if (!this.mediaConsentGranted()) {
      this.message.set('Media upload consent is required before viewing media.');
      return;
    }

    if (this.isConversionPending(artifact)) {
      this.message.set('Video conversion is still processing. Preview will be available when conversion completes.');
      return;
    }

    if (this.activePreviewId() === artifact.id) {
      this.activePreviewId.set(null);
      this.previewErrorMessage.set('');
      return;
    }

    this.previewErrorMessage.set('');

    let url = this.previewUrlMap.get(artifact.id);
    if (!url) {
      try {
        const blob = await firstValueFrom(this.mediaService.getMediaContent(artifact.id));
        const mediaType = this.resolveMediaType(blob, artifact);
        if (artifact.artifactType.toLowerCase() === 'video' && !this.canAttemptVideoPlayback(mediaType, artifact.fileName)) {
          this.activePreviewId.set(artifact.id);
          console.warn('[TeamMemberMedia] Browser cannot decode video type', { fileName: artifact.fileName, mediaType });
          this.previewErrorMessage.set(`This browser cannot decode '${artifact.fileName}' (${mediaType || 'unknown codec'}). Try MP4 (H.264/AAC).`);
          return;
        }

        url = this.createPlayableBlobUrl(blob, mediaType);
        this.previewUrlMap.set(artifact.id, url);
      } catch {
        this.message.set('Unable to load media preview.');
        return;
      }
    }

    this.activePreviewId.set(artifact.id);
  }

  previewUrl(id: number): string {
    return this.previewUrlMap.get(id) ?? '';
  }

  formatDate(raw: string): string {
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) {
      return raw;
    }

    return parsed.toLocaleString();
  }

  private async initializeContext(): Promise<void> {
    this.busy.set(true);
    this.message.set('');
    this.pendingSelectedUserFilterId = this.readSelectedUserFilterFromQuery();

    try {
      try {
        const org = await firstValueFrom(this.organizationService.getMyOrganization());
        if (org?.id) {
          this.organizationId.set(org.id);
          this.organizationName.set(org.displayName || org.legalName || '');
          await this.loadFilterOptions(org.id);
          await this.loadArtifacts();
          await this.loadNotificationPreference(org.id);
          return;
        }
      } catch {
        // Fall back to claim-based org resolution below.
      }

      const organizationId = await this.authService.getOrganizationIdAsync();
      if (organizationId) {
        this.organizationId.set(organizationId);
        await this.loadOrganizationName();
        await this.loadFilterOptions(organizationId);
        await this.loadArtifacts();
        await this.loadNotificationPreference(organizationId);
        return;
      }

      this.message.set('Could not resolve your organization context.');
    } finally {
      this.busy.set(false);
    }
  }

  openSmsConsentModal(): void {
    this.notificationPhoneNumber = this.notificationPreference()?.phoneNumber ?? '';
    this.showSmsConsentModal.set(true);
  }

  openMediaConsentModal(): void {
    this.showMediaConsentModal.set(true);
  }

  closeConsentModals(): void {
    this.showSmsConsentModal.set(false);
    this.showMediaConsentModal.set(false);
  }

  closeUploadErrorModal(): void {
    this.showUploadErrorModal.set(false);
  }

  private openUploadErrorModal(title: string, error: unknown): void {
    const parsed = this.parseUploadValidationError(error);
    this.uploadErrorTitle.set(title);
    this.uploadErrorCode.set(parsed.code ?? '');
    this.uploadErrorSummary.set(parsed.message || 'Please verify media type and file size limits.');
    this.uploadErrorTips.set(this.buildUploadErrorTips(parsed));
    this.showUploadErrorModal.set(true);
  }

  private parseUploadValidationError(error: unknown): UploadValidationErrorPayload {
    if (error instanceof HttpErrorResponse && error.error && typeof error.error === 'object') {
      return error.error as UploadValidationErrorPayload;
    }

    return {};
  }

  private buildUploadErrorTips(payload: UploadValidationErrorPayload): string[] {
    const tips: string[] = [];
    const details = payload.details;

    if (payload.code === 'UploadFileTooLarge' && typeof details?.maxBytes === 'number' && typeof details?.actualBytes === 'number') {
      tips.push(`Maximum allowed size: ${this.formatBytes(details.maxBytes)}.`);
      tips.push(`Uploaded file size: ${this.formatBytes(details.actualBytes)}.`);
    }

    if (payload.code === 'UploadFormatUnsupported') {
      if (details?.allowedExtensions?.length) {
        tips.push(`Supported file extensions: ${details.allowedExtensions.join(', ')}.`);
      }

      if (details?.allowedContentTypes?.length) {
        tips.push(`Supported media types: ${details.allowedContentTypes.join(', ')}.`);
      }
    }

    if (payload.code === 'UploadCorruptOrUnreadable') {
      tips.push('Try re-exporting the media file and upload again.');
      if (details?.helpUrl) {
        tips.push(`Need help? ${details.helpUrl}`);
      }
    }

    if (tips.length === 0) {
      tips.push('Verify media type and file size limits, then try again.');
    }

    return tips;
  }

  private formatBytes(bytes: number): string {
    if (bytes < 1024) {
      return `${bytes} bytes`;
    }

    const kb = bytes / 1024;
    if (kb < 1024) {
      return `${kb.toFixed(1)} KB`;
    }

    const mb = kb / 1024;
    return `${mb.toFixed(2)} MB`;
  }

  async applySmsConsent(consented: boolean): Promise<void> {
    const organizationId = this.organizationId();
    if (!organizationId) {
      return;
    }

    const existing = this.notificationPreference();
    const payload = {
      organizationId,
      phoneNumber: this.notificationPhoneNumber.trim(),
      smsNotificationsEnabled: consented,
      mediaUploadConsentEnabled: existing?.mediaUploadConsentEnabled ?? false
    };

    try {
      const updated = await firstValueFrom(this.notificationPreferenceService.upsertMineByOrganization(organizationId, payload));
      this.notificationPreference.set(updated);
      this.message.set(consented ? 'SMS notifications enabled.' : 'SMS notifications disabled.');
      this.closeConsentModals();
    } catch {
      this.message.set('Unable to update SMS notification consent.');
    }
  }

  async applyMediaConsent(consented: boolean): Promise<void> {
    const organizationId = this.organizationId();
    if (!organizationId) {
      return;
    }

    const existing = this.notificationPreference();
    const payload = {
      organizationId,
      phoneNumber: existing?.phoneNumber ?? '',
      smsNotificationsEnabled: existing?.smsNotificationsEnabled ?? false,
      mediaUploadConsentEnabled: consented
    };

    try {
      const updated = await firstValueFrom(this.notificationPreferenceService.upsertMineByOrganization(organizationId, payload));
      this.notificationPreference.set(updated);
      this.message.set(consented ? 'Media upload consent enabled.' : 'Media upload consent disabled.');

      if (!consented) {
        this.artifacts.set([]);
        this.activePreviewId.set(null);
        this.previewErrorMessage.set('');
        this.clearAllPreviews();
        this.stopConversionPolling();
      } else {
        await this.loadArtifacts();
      }

      this.closeConsentModals();
    } catch {
      this.message.set('Unable to update media upload consent.');
    }
  }

  private async loadNotificationPreference(organizationId: number): Promise<void> {
    try {
      const preference = await firstValueFrom(this.notificationPreferenceService.getMineByOrganization(organizationId));
      this.notificationPreference.set(preference);
      this.notificationPhoneNumber = preference.phoneNumber ?? '';
    } catch {
      this.notificationPreference.set(null);
      this.notificationPhoneNumber = '';
    }
  }

  private async loadOrganizationName(): Promise<void> {
    try {
      const org = await firstValueFrom(this.organizationService.getMyOrganization());
      this.organizationName.set(org.displayName || org.legalName || '');
    } catch {
      this.organizationName.set('');
    }
  }

  private async loadFilterOptions(defaultOrganizationId: number): Promise<void> {
    if (this.isSiteAdmin()) {
      try {
        const organizations = await firstValueFrom(this.organizationService.getAll());
        this.organizationFilterOptions = organizations
          .map(o => ({ id: o.id, name: o.displayName || o.legalName || `Organization ${o.id}` }))
          .sort((a, b) => a.name.localeCompare(b.name));
      } catch {
        this.organizationFilterOptions = [];
      }

      try {
        this.managedUsers = await firstValueFrom(this.userManagementService.getManagedUsers());
      } catch {
        this.managedUsers = [];
      }

      this.selectedOrganizationFilterId = null;
      this.selectedUserFilterId = null;
      this.populateUserFilterOptions();
      this.applyInitialSelectedUserFilter();
      return;
    }

    if (this.isOrgScopedAdmin()) {
      this.selectedOrganizationFilterId = defaultOrganizationId;
      try {
        this.managedUsers = await firstValueFrom(this.userManagementService.getManagedUsersByOrganization(defaultOrganizationId));
      } catch {
        this.managedUsers = [];
      }

      this.populateUserFilterOptions();
      this.applyInitialSelectedUserFilter();
      return;
    }

    this.organizationFilterOptions = [];
    this.userFilterOptions = [];
    this.managedUsers = [];
    this.selectedOrganizationFilterId = defaultOrganizationId;
    this.selectedUserFilterId = null;
  }

  private readSelectedUserFilterFromQuery(): number | null {
    const raw = this.route.snapshot.queryParamMap.get('selectedUserId');
    if (!raw) {
      return null;
    }

    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : null;
  }

  private applyInitialSelectedUserFilter(): void {
    const selectedUserId = this.pendingSelectedUserFilterId;
    if (!selectedUserId || !this.canFilterByUser()) {
      return;
    }

    const exists = this.userFilterOptions.some(u => u.id === selectedUserId);
    if (exists) {
      this.selectedUserFilterId = selectedUserId;
    }

    this.pendingSelectedUserFilterId = null;
  }

  private populateUserFilterOptions(): void {
    const scoped = this.selectedOrganizationFilterId
      ? this.managedUsers.filter(u => u.organizationId === this.selectedOrganizationFilterId)
      : this.managedUsers;

    const byUserId = new Map<number, MediaFilterUserOption>();
    for (const user of scoped) {
      if (byUserId.has(user.userId)) {
        continue;
      }

      const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
      const displayName = fullName || user.email || `User ${user.userId}`;
      const label = this.isSiteAdmin() && !this.selectedOrganizationFilterId
        ? `${displayName} (${user.organizationName})`
        : displayName;

      byUserId.set(user.userId, { id: user.userId, label });
    }

    this.userFilterOptions = [...byUserId.values()].sort((a, b) => a.label.localeCompare(b.label));

    if (this.selectedUserFilterId && !this.userFilterOptions.some(u => u.id === this.selectedUserFilterId)) {
      this.selectedUserFilterId = null;
    }
  }

  private async loadArtifacts(): Promise<void> {
    const organizationId = this.organizationId();
    if (!organizationId) {
      this.artifacts.set([]);
      this.stopConversionPolling();
      return;
    }

    if (this.isTeamMemberOnly() && !this.mediaConsentGranted()) {
      this.artifacts.set([]);
      this.clearAllPreviews();
      this.stopConversionPolling();
      return;
    }

    try {
      const items = this.isTeamMemberOnly()
        ? await firstValueFrom(this.mediaService.getOwnMediaByOrganization(organizationId))
        : await firstValueFrom(this.mediaService.getFilteredMedia(this.selectedOrganizationFilterId, this.selectedUserFilterId));

      this.artifacts.set(items);
      if (items.some(a => a.artifactType === 'Video' && this.isConversionPending(a))) {
        this.startConversionPolling();
      } else {
        this.stopConversionPolling();
      }
    } catch {
      this.message.set('Unable to load media list.');
      this.artifacts.set([]);
      this.stopConversionPolling();
    }
  }

  isConversionPending(artifact: TeamMemberArtifactDto): boolean {
    return TeamMemberMediaPageComponent.PendingStatuses.has((artifact.status ?? '').toLowerCase());
  }

  statusLabel(artifact: TeamMemberArtifactDto): string {
    if (this.isConversionPending(artifact)) {
      return artifact.status === 'ProcessingConversion' ? 'Converting…' : 'Queued';
    }

    if ((artifact.status ?? '').toLowerCase() === 'conversionfailed') {
      return 'Conversion failed';
    }

    return 'Ready';
  }

  statusClass(artifact: TeamMemberArtifactDto): string {
    if (this.isConversionPending(artifact)) {
      return 'status-pill status-pill--pending';
    }

    if ((artifact.status ?? '').toLowerCase() === 'conversionfailed') {
      return 'status-pill status-pill--error';
    }

    return 'status-pill status-pill--ready';
  }

  private startConversionPolling(): void {
    if (this.conversionPollingHandle) {
      return;
    }

    this.conversionPollingHandle = setInterval(async () => {
      if (!this.organizationId()) {
        this.stopConversionPolling();
        return;
      }

      await this.loadArtifacts();
    }, 3000);
  }

  private stopConversionPolling(): void {
    if (!this.conversionPollingHandle) {
      return;
    }

    clearInterval(this.conversionPollingHandle);
    this.conversionPollingHandle = null;
  }

  private clearPreviewFor(id: number): void {
    const existing = this.previewUrlMap.get(id);
    if (existing) {
      URL.revokeObjectURL(existing);
      this.previewUrlMap.delete(id);
    }

    if (this.activePreviewId() === id) {
      this.activePreviewId.set(null);
      this.previewErrorMessage.set('');
    }
  }

  private clearAllPreviews(): void {
    for (const url of this.previewUrlMap.values()) {
      URL.revokeObjectURL(url);
    }

    this.previewUrlMap.clear();
    this.activePreviewId.set(null);
    this.previewErrorMessage.set('');
  }

  clearMediaDecodeError(): void {
    if (this.previewErrorMessage()) {
      this.previewErrorMessage.set('');
    }
  }

  onMediaDecodeError(event: Event, artifact: TeamMemberArtifactDto): void {
    const media = event.target as HTMLMediaElement | null;
    const code = media?.error?.code;

    let detail = 'Unsupported format or codec.';
    if (code === MediaError.MEDIA_ERR_DECODE) {
      detail = 'Decode failed. The browser cannot decode this file.';
    } else if (code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
      detail = 'Source format is not supported by this browser.';
    }

    this.previewErrorMessage.set(`Unable to play '${artifact.fileName}'. ${detail}`);
    console.warn('[TeamMemberMedia] Media decode error', { fileName: artifact.fileName, code, detail });
  }

  private detectView(width: number): DeviceView {
    if (width < 768) {
      return 'mobile';
    }

    if (width < 1100) {
      return 'tablet';
    }

    return 'desktop';
  }

  private createPlayableBlobUrl(blob: Blob, mediaType: string): string {
    if (!mediaType) {
      return URL.createObjectURL(blob);
    }

    const typedBlob = new Blob([blob], { type: mediaType });
    return URL.createObjectURL(typedBlob);
  }

  private resolveMediaType(blob: Blob, artifact: TeamMemberArtifactDto): string {
    const fallbackType = artifact.artifactType === 'Sound' ? 'audio/mpeg' : this.resolveVideoContentType(artifact.fileName);
    const normalizedBlobType = (blob.type ?? '').trim().toLowerCase();
    const normalizedArtifactType = (artifact.contentType ?? '').trim().toLowerCase();
    const isVideo = artifact.artifactType.toLowerCase() === 'video';

    if (normalizedBlobType && normalizedBlobType !== 'application/octet-stream') {
      return isVideo
        ? this.normalizeVideoMediaType(normalizedBlobType, artifact.fileName)
        : normalizedBlobType;
    }

    const resolved = normalizedArtifactType || fallbackType;
    return isVideo
      ? this.normalizeVideoMediaType(resolved, artifact.fileName)
      : resolved;
  }

  private canAttemptVideoPlayback(mediaType: string, fileName: string): boolean {
    const lowerName = (fileName ?? '').toLowerCase();
    if (lowerName.endsWith('.wmv')) {
      return true;
    }

    return this.canBrowserPlayVideoType(mediaType);
  }

  private canBrowserPlayVideoType(mediaType: string): boolean {
    if (!mediaType) {
      return false;
    }

    const video = document.createElement('video');
    return video.canPlayType(mediaType) !== '';
  }

  private resolveVideoContentType(fileName: string): string {
    const lower = (fileName ?? '').toLowerCase();
    if (lower.endsWith('.wmv')) {
      return 'video/x-ms-wmv';
    }
    if (lower.endsWith('.mov')) {
      return 'video/quicktime';
    }
    return 'video/mp4';
  }

  private normalizeVideoMediaType(mediaType: string, fileName: string): string {
    const type = (mediaType ?? '').toLowerCase().trim();
    const lowerFile = (fileName ?? '').toLowerCase();

    if (!type || type === 'application/octet-stream') {
      return this.resolveVideoContentType(fileName);
    }

    if (lowerFile.endsWith('.wmv') || type === 'application/x-ms-wmv' || type === 'video/wmv') {
      return 'video/x-ms-wmv';
    }

    if (lowerFile.endsWith('.mov') || type === 'video/mov' || type.includes('quicktime')) {
      return 'video/quicktime';
    }

    if (lowerFile.endsWith('.mp4') || type.includes('mp4')) {
      return 'video/mp4';
    }

    return type;
  }
}
